import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Card, Typography, Space, Tag, Input, Select, Button, message, Drawer, Timeline, Modal, Statistic, Table } from 'antd';
import { SyncOutlined, CarOutlined, ExclamationCircleOutlined, CheckCircleOutlined, UserOutlined, UndoOutlined, ReloadOutlined } from '@ant-design/icons';
import { io } from 'socket.io-client';
import axios from 'axios';
import moment from 'moment';

import Breadcrumbs from "../../component/Breadcrumbs";
import DataTable from "../../component/ui/DataTable";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import FormSelect from "../../component/ui/FormSelect";
import { OutlineButton, ResetButton } from "../../component/ui/ActionButtons";
import { useAdminTable } from "../hooks/useAdminTable";

const { Title, Text } = Typography;
const { Option } = Select;

// Setup generic axios client for admin delivery ops since we don't have a pre-configured service file
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api',
});
apiClient.interceptors.request.use(config => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const AdminDeliveryOperations = () => {
    const [stats, setStats] = useState({ activeDeliveries: 0, completedDeliveries: 0, failedDeliveries: 0, totalStaff: 0, onlineStaff: 0 });
    const [socket, setSocket] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState(null);

    const [staffModalVisible, setStaffModalVisible] = useState(false);
    const [staffMetrics, setStaffMetrics] = useState([]);
    const [staffLoading, setStaffLoading] = useState(false);

    const [allStaff, setAllStaff] = useState([]);
    const [manualAssignStaffId, setManualAssignStaffId] = useState(null);

    const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
    const [selectedRejectionHistory, setSelectedRejectionHistory] = useState([]);

    const fetchDeliveries = async (params) => {
        let query = `?limit=${params.limit}&skip=${(params.page - 1) * params.limit}`;
        if (params.status !== 'all') query += `&status=${params.status}`;
        if (params.type !== 'all') query += `&type=${params.type}`;
        if (params.search) query += `&search=${params.search}`;

        const res = await apiClient.get(`/admin-delivery-ops/deliveries${query}`);
        if (res.data && res.data.success) {
            // Update drawer if open
            if (selectedDelivery) {
                const updated = res.data.data.find(d => d._id === selectedDelivery._id);
                if (updated) setSelectedDelivery(updated);
            }
            return { data: res.data.data, total: res.data.total };
        }
        return { data: [], total: 0 };
    };

    const {
        data: deliveries,
        setData: setDeliveries,
        total,
        loading,
        params: filters,
        setParams: setFilters,
        handleSearch,
        refresh
    } = useAdminTable(fetchDeliveries, "admin_delivery_ops", {
        page: 1, limit: 20, status: 'all', type: 'all', search: ''
    });

    const location = useLocation();

    useEffect(() => {
        const orderId = location.state?.highlightOrderId || location.state?.search;
        if (orderId) {
            setFilters(prev => ({ ...prev, search: orderId, page: 1 }));
        }
    }, [location.state?.highlightOrderId, location.state?.search]);

    useEffect(() => {
        const orderId = location.state?.highlightOrderId || location.state?.search;
        if (orderId && deliveries.length > 0) {
            const d = deliveries.find(x => 
                (x.orderId && (x.orderId._id === orderId || x.orderId.orderId === orderId)) || 
                x._id === orderId
            );
            if (d) {
                setSelectedDelivery(d);
                setDrawerVisible(true);
            }
        }
    }, [location.state, deliveries]);

    useEffect(() => {
        fetchStats();
        fetchAllStaff();

        // Socket logic
        const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
        if (token) {
            const socketUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '') : 'http://localhost:4000';
            const newSocket = io(socketUrl);
            
            newSocket.on('connect', () => {
                newSocket.emit('join', 'admin_delivery_ops');
            });

            newSocket.on('ADMIN_DELIVERY_UPDATE', (data) => {
                console.log("Admin Delivery Update:", data);
                // Refresh data silently
                fetchStats();
                refresh();
            });

            newSocket.on('DELIVERY_ALERT', (alert) => {
                message.warning({
                    content: `ALERT: ${alert.message}`,
                    duration: 10,
                    icon: <ExclamationCircleOutlined style={{ color: 'red' }} />
                });
            });

            setSocket(newSocket);
            return () => newSocket.disconnect();
        }
    }, []);

    const fetchStats = async () => {
        try {
            const res = await apiClient.get('/admin-delivery-ops/stats');
            if (res.data && res.data.success) {
                setStats(res.data.data);
            }
        } catch (error) {
            console.error("Failed to load stats", error);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const handleReassign = async (id) => {
        Modal.confirm({
            title: 'Are you sure you want to reassign this delivery?',
            content: 'This will strip the delivery from the current staff and push it back into the assignment pool.',
            okText: 'Yes, Reassign',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const res = await apiClient.post(`/admin-delivery-ops/reassign/${id}`);
                    if (res.data.success) {
                        message.success(res.data.message);
                        refresh();
                        setDrawerVisible(false);
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || "Failed to reassign");
                }
            }
        });
    };

    const fetchAllStaff = async () => {
        try {
            const res = await apiClient.get('/admin-delivery-ops/staff-performance?limit=1000');
            if (res.data.success) {
                setAllStaff(res.data.data);
            }
        } catch (err) {}
    };

    const handleManualAssign = async (id) => {
        if (!manualAssignStaffId) {
            message.warning("Please select a staff member first.");
            return;
        }
        try {
            const res = await apiClient.post(`/admin-delivery-ops/manual-assign/${id}`, {
                staffId: manualAssignStaffId
            });
            if (res.data.success) {
                message.success(res.data.message);
                refresh();
                setDrawerVisible(false);
                setManualAssignStaffId(null);
            }
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to manually assign delivery");
        }
    };

    const [staffDeliveriesModal, setStaffDeliveriesModal] = useState({ visible: false, type: '', data: [], loading: false, title: '' });

    const handleStaffCountClick = async (staffId, staffName, type) => {
        setStaffDeliveriesModal({ visible: true, type, data: [], loading: true, title: `${type} for ${staffName}` });
        try {
            let res;
            if (type === 'Ignored / Rejected') {
                res = await apiClient.get(`/admin-delivery-ops/rejections?limit=1000&staffId=${staffId}`);
                if (res.data.success) {
                    setStaffDeliveriesModal(prev => ({ ...prev, data: res.data.data, loading: false }));
                }
            } else {
                const status = type === 'Delivered' ? 'delivered' : 'active';
                res = await apiClient.get(`/admin-delivery-ops/deliveries?limit=1000&staffId=${staffId}&status=${status}`);
                if (res.data.success) {
                    setStaffDeliveriesModal(prev => ({ ...prev, data: res.data.data, loading: false }));
                }
            }
        } catch (error) {
            message.error("Failed to load staff deliveries");
            setStaffDeliveriesModal(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchStaffMetrics = async () => {
        setStaffModalVisible(true);
        setStaffLoading(true);
        try {
            const res = await apiClient.get('/admin-delivery-ops/staff-performance');
            if (res.data.success) {
                setStaffMetrics(res.data.data);
            }
        } catch (err) {
            message.error("Failed to load staff performance");
        } finally {
            setStaffLoading(false);
        }
    };

    const columns = [
        {
            title: 'Delivery ID / Order ID',
            key: 'id',
            render: (_, record) => (
                <div>
                    <Text strong>{record.id}</Text><br/>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{record.orderId?.orderId || 'N/A'}</Text>
                    {record.type === 'Return' || record.type === 'RETURN_PICKUP' ? 
                        <Tag color="magenta" style={{ marginLeft: 5 }}>Return</Tag> : null
                    }
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'blue';
                if (['delivered', 'Delivered'].includes(status)) color = 'green';
                else if (['failed', 'Failed', 'cancelled', 'Cancelled'].includes(status)) color = 'red';
                else if (['picked_up', 'out_for_delivery'].includes(status?.toLowerCase())) color = 'orange';
                return <Tag color={color}>{status?.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Delivery Staff (Live Tracking)',
            key: 'staff',
            render: (_, record) => {
                const history = record.assignmentHistory || [];
                const currentAttempt = history.length > 0 ? history[history.length - 1] : null;
                const rejectedStaff = history.filter(ah => ah.status === 'rejected' || ah.status === 'timeout');

                return (
                    <div>
                        {record.assignedStaffId ? (
                            <div style={{ marginBottom: 4 }}>
                                {['assigned', 'Assigned'].includes(record.status) ? (
                                    <Tag color="orange">PENDING ACCEPTANCE</Tag>
                                ) : (
                                    <Tag color="green">ACCEPTED</Tag>
                                )}<br/>
                                <Text strong>{record.assignedStaffId.firstName} {record.assignedStaffId.lastName}</Text><br/>
                                <Text type="secondary" style={{ fontSize: '12px' }}>{record.assignedStaffId.phone}</Text>
                            </div>
                        ) : currentAttempt && currentAttempt.status === 'pending' ? (
                            <div style={{ marginBottom: 4 }}>
                                <Tag color="orange" icon={<SyncOutlined spin />}>PINGING</Tag><br/>
                                <Text strong>{currentAttempt.staffId?.firstName || 'Staff'} {currentAttempt.staffId?.lastName || ''}</Text>
                            </div>
                        ) : (
                            <Text type="warning" strong>Unassigned / Searching...</Text>
                        )}

                        {rejectedStaff.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="danger" style={{ fontSize: '11px', display: 'block', fontWeight: 600 }}>Ignored/Rejected By:</Text>
                                <Space wrap size={[4, 4]}>
                                    <Tag 
                                        color="red" 
                                        style={{ cursor: 'pointer', margin: 0, fontSize: '10px', fontWeight: 500 }}
                                        onClick={() => {
                                            setSelectedRejectionHistory(rejectedStaff);
                                            setRejectionModalVisible(true);
                                        }}
                                    >
                                        {rejectedStaff.length} staff ignored (Last: {rejectedStaff[rejectedStaff.length - 1].staffId?.firstName || 'Unknown'})
                                    </Tag>
                                </Space>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_, record) => (
                record.orderId?.user ? 
                <div>
                    <Text>{record.orderId.user.firstName}</Text><br/>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{record.orderId.user.phone}</Text>
                </div> : "N/A"
            )
        },
        {
            title: 'Last Updated',
            key: 'updatedAt',
            render: (_, record) => moment(record.updatedAt).fromNow()
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <OutlineButton 
                    text="Details" 
                    onClick={() => {
                        setSelectedDelivery(record);
                        setDrawerVisible(true);
                    }} 
                />
            )
        }
    ];

    const staffColumns = [
        { title: 'Name', key: 'name', render: (_, r) => <div><Text strong>{r.name}</Text><br/><Text type="secondary" style={{ fontSize: 12 }}>{r.phone}</Text></div> },
        { title: 'Status', key: 'isOnline', render: (_, r) => <Tag color={r.isOnline ? 'green' : 'red'}>{r.isOnline ? 'Online' : 'Offline'}</Tag> },
        { 
            title: 'Active Load', 
            dataIndex: 'currentOrders', 
            key: 'load',
            render: (val, r) => val > 0 ? <a onClick={() => handleStaffCountClick(r._id, r.name, 'Active Load')}>{val}</a> : val 
        },
        { 
            title: 'Delivered', 
            dataIndex: 'successfulDeliveries', 
            key: 'success',
            render: (val, r) => val > 0 ? <a onClick={() => handleStaffCountClick(r._id, r.name, 'Delivered')}>{val}</a> : val 
        },
        { 
            title: 'Ignored / Rejected', 
            dataIndex: 'totalRejectedOrIgnored', 
            key: 'ignored', 
            render: (val, r) => val > 0 ? <a style={{ color: 'red' }} onClick={() => handleStaffCountClick(r._id, r.name, 'Ignored / Rejected')}>{val}</a> : <Text type="danger">{val}</Text>
        },
        { title: 'Acceptance Rate', key: 'rate', render: (_, r) => `${r.acceptanceRate || 100}%` }
    ];

    return (
        <div className="product-option-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden pcu-margin-bottom-24">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom">
                    <div className="pcu-flex pcu-justify-between pcu-items-start">
                        <div>
                            <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                                Delivery Operations Center
                            </h1>
                            <Text type="secondary">Monitor fleet, assignments, and real-time alerts</Text>
                        </div>
                        <div className="pcu-flex pcu-gap-12 pcu-items-center">
                            <OutlineButton icon={<UserOutlined />} onClick={fetchStaffMetrics} text="Staff Performance" />
                            <ResetButton icon={<ReloadOutlined />} onClick={() => refresh()} text="Refresh" />
                        </div>
                    </div>
                </div>

                <div className="pcu-padding-24">
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={8} lg={4}>
                            <div className="pcu-border pcu-radius-8 pcu-padding-16">
                                <Statistic title="Online Staff" value={stats.onlineStaff} suffix={`/ ${stats.totalStaff}`} prefix={<CarOutlined style={{ color: '#52c41a' }} />} />
                            </div>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <div className="pcu-border pcu-radius-8 pcu-padding-16">
                                <Statistic title="Active Deliveries" value={stats.activeDeliveries} prefix={<SyncOutlined spin style={{ color: '#1890ff' }} />} />
                            </div>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <div className="pcu-border pcu-radius-8 pcu-padding-16">
                                <Statistic title="Completed Today" value={stats.completedDeliveries} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} />
                            </div>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <div className="pcu-border pcu-radius-8 pcu-padding-16">
                                <Statistic title="Failed / Cancelled" value={stats.failedDeliveries} prefix={<ExclamationCircleOutlined style={{ color: '#f5222d' }} />} />
                            </div>
                        </Col>
                    </Row>

                    <FilterBar>
                        <FormSelect 
                            value={filters.status} 
                            onChange={(val) => handleFilterChange('status', val)} 
                            options={[
                                { value: 'all', label: 'All Statuses' },
                                { value: 'active', label: 'Active (Assigned -> Out For Delivery)' },
                                { value: 'pending', label: 'Pending (Unassigned)' },
                                { value: 'delivered', label: 'Delivered' },
                                { value: 'failed', label: 'Failed / Cancelled' },
                                { value: 'return', label: 'Return Deliveries' }
                            ]}
                        />
                        <FormSelect 
                            value={filters.type} 
                            onChange={(val) => handleFilterChange('type', val)} 
                            options={[
                                { value: 'all', label: 'All Types' },
                                { value: 'Normal', label: 'Normal' },
                                { value: 'Return', label: 'Return / Exchange' }
                            ]}
                        />
                        <div style={{ flex: 2, minWidth: '250px' }}>
                            <SearchInput 
                                placeholder="Search by Order ID or Display ID" 
                                onSearch={handleSearch}
                            />
                        </div>
                    </FilterBar>

                    <DataTable 
                        columns={columns} 
                        data={deliveries} 
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            current: filters.page,
                            pageSize: filters.limit,
                            total,
                            onChange: (page) => {
                                setFilters(prev => ({ ...prev, page }));
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            },
                            onShowSizeChange: (c, size) => setFilters(prev => ({ ...prev, limit: size, page: 1 }))
                        }}
                    />
                </div>
            </div>

            <Drawer
                title={`Delivery Details - ${selectedDelivery?.id}`}
                placement="right"
                width={500}
                onClose={() => setDrawerVisible(false)}
                visible={drawerVisible}
            >
                {selectedDelivery && (
                    <div>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={5}>Current State</Title>
                            <Tag color="blue" style={{ fontSize: 16, padding: '4px 8px' }}>
                                {selectedDelivery.status?.toUpperCase()}
                            </Tag>
                            {selectedDelivery.assignedStaffId && !['delivered', 'failed', 'cancelled'].includes(selectedDelivery.status?.toLowerCase()) && (
                                <Button 
                                    danger 
                                    type="dashed" 
                                    icon={<UndoOutlined />} 
                                    style={{ marginLeft: 16 }}
                                    onClick={() => handleReassign(selectedDelivery._id)}
                                >
                                    Force Reassign
                                </Button>
                            )}

                            {!['delivered', 'failed', 'cancelled'].includes(selectedDelivery.status?.toLowerCase()) && (
                                <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Manual Assignment</Text>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Select
                                            placeholder="Select delivery staff..."
                                            style={{ width: 'calc(100% - 120px)' }}
                                            onChange={(val) => setManualAssignStaffId(val)}
                                            value={manualAssignStaffId}
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {allStaff.map(s => (
                                                <Option key={s._id} value={s._id}>
                                                    {s.name} <span style={{ color: s.isOnline ? '#52c41a' : '#f5222d' }}>{s.isOnline ? '(Online)' : '(Offline)'}</span>
                                                </Option>
                                            ))}
                                        </Select>
                                        <Button 
                                            type="primary" 
                                            onClick={() => handleManualAssign(selectedDelivery._id)}
                                        >
                                            Force Assign
                                        </Button>
                                    </Space.Compact>
                                </div>
                            )}
                        </div>

                        <Title level={5}>Delivery Timeline</Title>
                        <Timeline>
                            <Timeline.Item color="green">
                                <strong>Created</strong> <br/>
                                <Text type="secondary">{moment(selectedDelivery.createdAt).format('DD MMM YYYY, hh:mm A')}</Text>
                            </Timeline.Item>
                            
                            {selectedDelivery.assignmentHistory?.map((ah, idx) => (
                                <Timeline.Item key={idx} color={ah.status === 'rejected' || ah.status === 'timeout' ? 'red' : 'blue'}>
                                    <strong>Assignment {ah.attemptNumber} - {ah.status.toUpperCase()}</strong><br/>
                                    <Text>{ah.staffId?.firstName} {ah.staffId?.lastName}</Text><br/>
                                    <Text type="secondary">{moment(ah.assignedAt).format('DD MMM YYYY, hh:mm A')}</Text>
                                    {ah.rejectionReason && <div><Text type="danger">Reason: {ah.rejectionReason}</Text></div>}
                                </Timeline.Item>
                            ))}

                            {selectedDelivery.history?.map((h, idx) => (
                                <Timeline.Item key={`hist-${idx}`} color="blue">
                                    <strong>{h.status?.toUpperCase()}</strong><br/>
                                    <Text>{h.comment || 'Status updated'}</Text> {h.updatedBy ? <Text type="secondary">- by {h.updatedBy.firstName}</Text> : null}<br/>
                                    <Text type="secondary">{moment(h.timestamp).format('DD MMM YYYY, hh:mm A')}</Text>
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    </div>
                )}
            </Drawer>

            <Modal
                title="Delivery Staff Performance"
                visible={staffModalVisible}
                onCancel={() => setStaffModalVisible(false)}
                footer={null}
                width={800}
            >
                <DataTable 
                    data={staffMetrics} 
                    columns={staffColumns} 
                    rowKey="_id" 
                    loading={staffLoading}
                    pagination={{ pageSize: 10 }}
                />
            </Modal>

            <Modal
                title={staffDeliveriesModal.title}
                visible={staffDeliveriesModal.visible}
                onCancel={() => setStaffDeliveriesModal(prev => ({ ...prev, visible: false }))}
                footer={null}
                width={700}
            >
                <DataTable
                    data={staffDeliveriesModal.data}
                    rowKey={(r) => r._id || r.deliveryId}
                    loading={staffDeliveriesModal.loading}
                    pagination={{ pageSize: 10 }}
                    columns={[
                        {
                            title: 'Delivery ID',
                            dataIndex: staffDeliveriesModal.type === 'Ignored / Rejected' ? 'deliveryId' : 'id',
                            key: 'id'
                        },
                        {
                            title: 'Order ID',
                            key: 'orderId',
                            render: (_, r) => staffDeliveriesModal.type === 'Ignored / Rejected' ? r.orderId : r.orderId?.orderId
                        },
                        {
                            title: 'Status / Reason',
                            key: 'status',
                            render: (_, r) => {
                                if (staffDeliveriesModal.type === 'Ignored / Rejected') {
                                    return <div><Tag color="red">{r.status?.toUpperCase()}</Tag><br/><Text type="secondary" style={{fontSize: 12}}>{r.reason || 'Timeout'}</Text></div>
                                } else {
                                    let color = 'blue';
                                    if (['delivered', 'Delivered'].includes(r.status)) color = 'green';
                                    else if (['failed', 'cancelled'].includes(r.status)) color = 'red';
                                    return <Tag color={color}>{r.status?.toUpperCase()}</Tag>
                                }
                            }
                        },
                        {
                            title: 'Time',
                            key: 'time',
                            render: (_, r) => {
                                const t = staffDeliveriesModal.type === 'Ignored / Rejected' ? r.time : r.createdAt;
                                return moment(t).format('DD MMM, hh:mm A');
                            }
                        },
                        {
                            title: 'Action',
                            key: 'action',
                            render: (_, r) => (
                                <Button size="small" type="primary" onClick={() => {
                                    // Make sure it has enough data to show details
                                    const fullRecord = deliveries.find(d => d._id === r._id) || r;
                                    setSelectedDelivery(fullRecord);
                                    setStaffDeliveriesModal(prev => ({ ...prev, visible: false }));
                                    setStaffModalVisible(false);
                                    setDrawerVisible(true);
                                }}>
                                    View
                                </Button>
                            )
                        }
                    ]}
                />
            </Modal>

            <Modal
                title="Ignored / Rejected Staff History"
                visible={rejectionModalVisible}
                onCancel={() => setRejectionModalVisible(false)}
                centered
                footer={[
                    <Button key="close" onClick={() => setRejectionModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={650}
            >
                <Table
                    dataSource={selectedRejectionHistory}
                    rowKey={(record, index) => record._id || index}
                    pagination={false}
                    size="small"
                    columns={[
                        {
                            title: 'Attempt',
                            key: 'attempt',
                            width: 100,
                            render: (_, record, index) => <Tag color="blue">Attempt #{record.attemptNumber || index + 1}</Tag>
                        },
                        {
                            title: 'Staff Name',
                            key: 'name',
                            render: (_, record) => `${record.staffId?.firstName || 'Unknown'} ${record.staffId?.lastName || ''}`
                        },
                        {
                            title: 'Phone Number',
                            key: 'phone',
                            render: (_, record) => record.staffId?.phone || 'N/A'
                        },
                        {
                            title: 'Status / Reason',
                            dataIndex: 'status',
                            key: 'status',
                            render: (status, record) => {
                                const color = status === 'timeout' ? 'warning' : 'error';
                                const label = status === 'timeout' ? 'Timeout (Missed)' : 'Rejected';
                                return (
                                    <div>
                                        <Tag color={color}>{label.toUpperCase()}</Tag>
                                        {record.rejectionReason && <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: 4 }}>Reason: {record.rejectionReason}</div>}
                                    </div>
                                );
                            }
                        }
                    ]}
                />
            </Modal>
        </div>
    );
};

export default AdminDeliveryOperations;
